import type { Matter, Task, Activity } from "@/shared/cache";
import {
	isTaylorDay,
	isNamanHowell,
	isPregODonnell,
} from "@/shared/utils/firm";

// Default group ids — mirror EntryForm.tsx.
const TAYLOR_DAY_DEFAULT_TASK_GROUP = "9a69ea86-d958-482c-b2fd-35c6ee47830d";
const TAYLOR_DAY_DEFAULT_ACTIVITY_GROUP = "dca1c471-21cb-4c00-8e62-d075d854446d";
const NAMAN_HOWELL_DEFAULT_TASK_GROUP = "54bc344d-0b93-93e4-c912-69a39322a816";

/**
 * Firm-aware task list. Taylor Day and Naman Howell scope tasks to the
 * matter's `task_group` (falling back to a firm default when no matter is
 * selected). All other firms see the full task list.
 *
 * Shared between EntryForm and the merge/group dialogs so a matter chip in
 * the dialog narrows the task picker the same way it does in add/edit.
 */
export function availableTasksForMatter(
	tasks: Task[],
	matters: Matter[],
	matterId: string | null | undefined,
): Task[] {
	const selected = matterId ? matters.find((m) => m.matter_id === matterId) ?? null : null;

	if (isTaylorDay()) {
		if (!selected) return tasks.filter((t) => t.task_group === TAYLOR_DAY_DEFAULT_TASK_GROUP);
		if (!selected.task_group?.trim()) return [];
		return tasks.filter((t) => t.task_group === selected.task_group);
	}

	if (isNamanHowell()) {
		if (!selected) return tasks.filter((t) => t.task_group === NAMAN_HOWELL_DEFAULT_TASK_GROUP);
		if (!selected.task_group?.trim()) return [];
		return tasks.filter((t) => t.task_group === selected.task_group);
	}

	return tasks;
}

/**
 * Firm-aware activity list. Preg O'Donnell, Taylor Day, and Naman Howell each
 * scope activities to the matter's `activity_group`; others see the full list.
 */
export function availableActivitiesForMatter(
	activities: Activity[],
	matters: Matter[],
	matterId: string | null | undefined,
): Activity[] {
	const selected = matterId ? matters.find((m) => m.matter_id === matterId) ?? null : null;

	if (isPregODonnell()) {
		if (!selected) return activities.filter((a) => a.activity_group === "ABA");
		if (!selected.activity_group?.trim()) return [];
		return activities.filter((a) => a.activity_group === selected.activity_group);
	}

	if (isTaylorDay()) {
		if (!selected) {
			return activities.filter((a) => a.activity_group === TAYLOR_DAY_DEFAULT_ACTIVITY_GROUP);
		}
		if (!selected.activity_group?.trim()) return [];
		return activities.filter((a) => a.activity_group === selected.activity_group);
	}

	if (isNamanHowell()) {
		if (selected?.activity_group?.trim()) {
			return activities.filter((a) => a.activity_group === selected.activity_group);
		}
		return activities;
	}

	return activities;
}
